import type { Messages } from './messages';

/**
 * Copy for `src/app/models/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `models.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const models = {
  /* ---- §11 التسعيرة: سعر واحد لكل النظام ------------------------------ */
  'models.rateHint':
    'سعر واحد لكل نموذج، تُحاسَب عليه كل المنشآت. المنشأة التي لها عقد خاص تُسعَّر من تبويب نماذجها، وسعرها يسبق هذا. النموذج بلا سعر لا يُضاف إلى أي حدّ — وهذا ما يعنيه «لم يُخبرنا أحد بالسعر»، لا أنه مجاني.',
  'models.rateUnpriced': 'لم يُسعَّر {count} من النماذج. إجاباتها لا تُحتسب في أي حدّ لأي منشأة.',
  'models.rateUnset': 'بلا سعر',
  'models.rateAtInput': 'بسعر الدخل',
  'models.rateCacheAtInput': 'لا سعر كاش لهذا النموذج، فيُحاسَب بسعر الدخل — وهو تقدير أعلى لا أقل.',
  'models.rateInForce': 'دخل {input} · خرج {output} ر.س / مليون',
  'models.rateSetFor': 'تحديد سعر {model}',
  'models.rateChangeFor': 'تغيير سعر {model}',
  'models.rateDialogTitle': 'سعر {model}',
  'models.rateDialogHint': 'الحفظ يضيف سعرًا جديدًا يسري من الآن، ولا يعدّل السعر القائم: الرسالة تُحاسَب بالسعر الساري لحظة كُتبت.',
  'models.rateSave': 'حفظ سعرًا جديدًا',
  'models.rateInput': 'سعر الدخل',
  'models.rateOutput': 'سعر الخرج',
  'models.rateCacheRead': 'قراءة من الكاش',
  'models.rateCacheReadHint': 'اتركه فارغًا إن لم يصلك السعر؛ عندها يُحاسَب بسعر الدخل، وهو تقدير أعلى لا أقل.',
  'models.rateCacheWrite': 'كتابة إلى الكاش',
  'models.rateCacheWriteHint': 'لا تُحاسِب OpenAI عليها؛ تخزينها تلقائي. اتركه فارغًا لنماذجها.',
  'models.rateSource': 'مصدر السعر',
  'models.rateSourceHint': 'المعلَن وحده يمكن مراجعته لاحقًا على صفحة المزوّد.',
  'models.rateList': 'السعر المعلَن',
  'models.rateContract': 'سعر تعاقدي',
  'models.rateFillPublished': 'عبّئ من المعلَن ({readAt})',
  'models.ratePublished': 'المعلَن: دخل ${input} · خرج ${output} لكل مليون، بسعر تحويل {rate}.',
  'models.rateInvalid': 'راجع الحقول المعلَّمة قبل الحفظ.',
  'models.rateError.required': 'سعرا الدخل والخرج مطلوبان — هذه التسعيرة تُحاسِب كل المنشآت.',
  'models.rateError.notANumber': 'أدخل رقمًا.',
  'models.rateError.negative': 'لا يصحّ سعر بالسالب.',

  /* ---- سعر الصرف: مكان واحد، وكل الأسعار المعلَنة تُشتقّ منه -------- */
  'models.fxTitle': 'سعر صرف الريال مقابل الدولار',
  'models.fxHint':
    'كل سعر معلَن في الجدول أدناه هو السعر بالدولار مضروبًا في هذا الرقم. تغييره هنا يعيد اشتقاق كل الأسعار المعلَنة، ولا يمسّ الأسعار التعاقدية — تلك اتُّفق عليها بالريال ابتداءً.',
  'models.fxValue': '{rate} ر.س لكل دولار',
  'models.fxUnset': 'لم يُضبط بعد',
  'models.fxSince': 'ساري منذ {when}',
  'models.fxBy': 'ضبطه {who}',
  'models.fxChange': 'تغيير سعر الصرف',
  'models.fxField': 'سعر الصرف',
  'models.fxFieldHint': 'كم ريالًا يشتري الدولار الواحد. الربط الثابت 3.75.',
  'models.fxNote': 'سبب التغيير',
  'models.fxNoteHint': 'يُحفظ مع كل سعر يُعاد اشتقاقه.',
  'models.fxSave': 'احفظ وأعد تسعير المعلَن',
  'models.fxInvalid': 'أدخل رقمًا أكبر من صفر.',
  'models.fxWarning':
    'هذا يغيّر أسعار كل النماذج المسعَّرة من القائمة المعلَنة دفعةً واحدة. لا يُعاد تسعير ما مضى: كل رسالة تبقى محاسَبة بالسعر الساري لحظة كُتبت.',
  'models.fxRepriced': 'تم. أُعيد اشتقاق {count} سعرًا بالسعر الجديد.',
  'models.fxRepricedNone': 'تم. لم يتغيّر شيء — هذا هو سعر الصرف الساري أصلًا.',

  /* ---- العرض: ما تقدّمه المنصّة للمنشآت أصلًا ------------------------- */
  'models.offerOn': 'معروض',
  'models.offerOff': 'مسحوب',
  'models.offerSwitch': 'عرض {model} على المنشآت',
  'models.offerFailed': 'تعذّر تغيير العرض. أعد المحاولة.',
  'models.offerFilter': 'ما يُعرض من النماذج',
  'models.offerFilterOffered': 'المعروضة',
  'models.offerFilterWithdrawn': 'المسحوبة',
  'models.offerFilterAll': 'الكل',
  'models.rateSearch': 'ابحث عن نموذج بالاسم أو المعرّف',
  'models.rateNoMatch': 'لا نموذج يطابق البحث.',

  'models.title': 'النماذج',
  'models.subtitle': 'ما تعرضه المنصّة على المنشآت، وبكم تُحاسَب عليه. ما يُسحب من هنا لا يظهر لأي منشأة ولا يردّه أي API.',

  /* Where enabling happens */
  'models.whereTitle': 'العرض من هنا، والاعتماد داخل كل منشأة',
  'models.where': 'ما تُسحبه هنا يختفي من كل المنشآت دفعةً واحدة. وما يُعرض تختار كل منشأة اعتماده أو تركه من تبويب «النماذج» لديها.',
  'models.whereAction': 'المنشآت',

  /* Table */
  'models.caption': 'نماذج {provider}',
  'models.col.model': 'النموذج',
  'models.col.tier': 'الجودة',
  'models.col.cost': 'الكلفة النسبية',
  'models.col.context': 'نافذة السياق',
  'models.col.description': 'الوصف',
  'models.col.offered': 'معروض',
  'models.col.input': 'دخل / مليون',
  'models.col.output': 'خرج / مليون',
  'models.col.cacheRead': 'قراءة كاش / مليون',
  'models.col.cacheWrite': 'كتابة كاش / مليون',
  'models.col.source': 'مصدر السعر',
  'models.col.actions': 'إجراءات',
  'models.noDescription': 'بلا وصف',
  'models.tokens': '{count} رمز',
  'models.costHint': 'الكلفة نسبية بين النماذج، وليست سعرًا.',
  'models.empty': 'لا نماذج في الكتالوج',
  'models.emptyBody': 'الكتالوج جزء من شيفرة المنصّة؛ قائمة فارغة تعني خللًا في النشر. أعد المحاولة، وإن تكرّر فأبلغ فريق المنصّة.',
  'models.providerEmpty': 'لا نماذج لهذا المزوّد',
} as const satisfies Messages<'models'>;
